
import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfToday, nextMonday } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";

const DEFAULT_FEE = 35;
const DEFAULT_TIME = "20:00"; // 8:00 PM
const DEFAULT_RESERVED_SLOTS = 5;

export const TimeSlotForm = () => {
  const [date, setDate] = useState<Date>();
  const [startTime, setStartTime] = useState(DEFAULT_TIME);
  const [maxPlayers, setMaxPlayers] = useState("42");
  const [maxGuests, setMaxGuests] = useState("2");
  const [fee, setFee] = useState(DEFAULT_FEE.toString());
  const [reservedSlots, setReservedSlots] = useState(DEFAULT_RESERVED_SLOTS.toString());
  const [reservedPaidSlots, setReservedPaidSlots] = useState(DEFAULT_RESERVED_SLOTS.toString());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Function to get next Monday
  const getNextMonday = () => {
    const today = new Date();
    return nextMonday(today);
  };

  // Set initial date to next Monday when component mounts
  useEffect(() => {
    setDate(getNextMonday());
  }, []);

  // Custom CSS for today's date and selected date
  const modifiersStyles = {
    today: {
      border: '2px solid #333333',
      borderRadius: '4px',
      backgroundColor: 'transparent',
      color: 'inherit',
      ':hover': {
        backgroundColor: 'transparent',
        color: 'inherit',
        border: '2px solid #333333',
      },
      ':focus': {
        backgroundColor: 'transparent',
        color: 'inherit',
        border: '2px solid #333333',
      },
      ':active': {
        backgroundColor: 'transparent',
        color: 'inherit',
        border: '2px solid #333333',
      }
    },
    selected: {
      backgroundColor: '#1E2873',
      color: '#FFFFFF',
      borderRadius: '4px',
    }
  };

  const handleCreateTimeSlot = async () => {
    if (!date) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    // Create a new Date object with the selected date's year, month, and day
    // This ensures we're working with the correct date regardless of timezone
    const selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const newTimeSlot = {
      date: format(selectedDate, 'yyyy-MM-dd'),
      start_time: startTime,
      max_players: parseInt(maxPlayers),
      max_guests_per_player: parseInt(maxGuests),
      fee: parseFloat(fee),
      booked_players: 0,
      paid_players: 0,
      reserved_slots: parseInt(reservedSlots),
      reserved_paid_slots: parseInt(reservedPaidSlots)
    };

    const { error } = await supabase
      .from('time_slots_created')
      .insert([newTimeSlot]);

    if (error) {
      console.error('Error creating time slot:', error);
      
      // Check if it's a unique constraint violation
      if (error.code === '23505') {
        toast({
          title: "Error",
          description: "A time slot already exists for this date and time",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to create time slot",
          variant: "destructive",
        });
      }
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['timeSlotsCreated'] });
    queryClient.invalidateQueries({ queryKey: ['timeSlotsToSelect'] });
    toast({
      title: "Success",
      description: "Time slot created successfully",
    });
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <h2 className="text-xl font-semibold mb-6">Create Time Slot</h2>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">Select Date</label>
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            className="rounded-md border"
            weekStartsOn={1}
            defaultMonth={getNextMonday()}
            modifiers={{
              today: startOfToday(),
            }}
            modifiersStyles={modifiersStyles}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Start at
            </label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Fees (AED)
            </label>
            <Input
              type="number"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Max. Players
            </label>
            <Input
              type="number"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Max. Guests/Player
            </label>
            <Input
              type="number"
              value={maxGuests}
              onChange={(e) => setMaxGuests(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Reserved
            </label>
            <Input
              type="number"
              value={reservedSlots}
              onChange={(e) => setReservedSlots(e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Paid
            </label>
            <Input
              type="number"
              value={reservedPaidSlots}
              onChange={(e) => setReservedPaidSlots(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <Button
          onClick={handleCreateTimeSlot}
          className="w-full bg-primary hover:bg-primary/90"
        >
          Create Time Slot
        </Button>
      </div>
    </div>
  );
};
